import { Button } from '@edsn/pie-components';
import { type ReactNode, useEffect } from 'react';
import type { DefaultValues, SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCurrentOrganisation } from '#pie/auth';
import { Card } from '#pie/components/card/Card';
import { InfoDialog } from '#pie/components/info-dialog/InfoDialog';
import { Page, PageHeader } from '#pie/components/page/Page';
import { SkeletonLines } from '#pie/components/skeleton-lines/SkeletonLines';
import { Stack } from '#pie/components/stack/Stack';
import { Text } from '#pie/components/text/Text';
import type { CreateUpdateContactDetailsRequest } from '#pie/internal/api/pie-bff';
import {
  AuthorizationRoles,
  MarketRole,
  useGetTeam,
} from '#pie/internal/api/pie-bff';
import { isDefined } from '#pie/utils/isDefined';
import { typedFormFields } from '#pie/utils/typedFormFields';

const { FormText, FormCheckbox, FormPhone } =
  typedFormFields<CreateUpdateContactDetailsRequest>();

interface Props {
  onSubmit: SubmitHandler<CreateUpdateContactDetailsRequest>;
  isLoading: boolean;
  defaultValues?: DefaultValues<CreateUpdateContactDetailsRequest>;
  action?: ReactNode;
  confirmationDialog?: ReactNode;
}

export const FormOrganisationUser = ({
  onSubmit,
  isLoading,
  defaultValues,
  action,
  confirmationDialog,
}: Props) => {
  const formMethods = useForm<CreateUpdateContactDetailsRequest>({
    defaultValues,
  });

  useEffect(() => {
    formMethods.reset(defaultValues);
  }, [defaultValues]);

  const { t } = useTranslation();
  const { t: tom } = useTranslation('organisationManagement');
  const { data, isLoading: isLoadingTeams } = useGetTeam();
  const { currentAccount } = useCurrentOrganisation();
  const isUpdate = defaultValues && Object.keys(defaultValues).length > 0;

  const marketRolesWithRegistryReadAll: MarketRole[] = [
    MarketRole.LV,
    MarketRole.RNB,
    MarketRole.GDS,
    MarketRole.LNB,
  ];

  const canSeeRegistryReadAll =
    !!currentAccount &&
    marketRolesWithRegistryReadAll.includes(currentAccount.marketRole);

  const title = isUpdate
    ? tom('header.title.user', {
        user: `${defaultValues.firstName} ${defaultValues.lastName}`.trim(),
      })
    : tom('header.title.new_user');

  return (
    <Page>
      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit(onSubmit)} aria-label={title}>
          <PageHeader
            title={title}
            previousText={t('common.button.back')}
            backHref="/beheer/gebruikersbeheer"
          >
            <Stack direction="row" gap="md">
              {action}
              <Button
                type="submit"
                isLoading={isLoading}
                action="secondary"
                emphasis="bold"
                disabled={isUpdate && !formMethods.formState.isDirty}
              >
                {isUpdate
                  ? tom('header.button.submit_changes')
                  : tom('header.button.submit.create')}
              </Button>
            </Stack>
          </PageHeader>
          <Stack gap="lg" className="mx-auto my-6 max-w-4xl">
            <Card
              size="md"
              heading={
                <Text as="h6" variant="h6">
                  {t('common.personal_info')}
                </Text>
              }
            >
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                <FormText
                  name="firstName"
                  label={t('common.first_name')}
                  fullWidth
                  rules={{ required: true, maxLength: 16 }}
                />
                <FormText
                  name="lastName"
                  label={t('common.last_name_prefix')}
                  fullWidth
                  rules={{ required: true }}
                />
                <FormText
                  name="email"
                  type="email"
                  label={t('common.email')}
                  fullWidth
                  rules={{ required: true }}
                />
                <FormPhone name="phone" />
              </div>
            </Card>
            <Card
              size="md"
              heading={
                <Text as="h6" variant="h6">
                  {t('common.roles')}
                </Text>
              }
            >
              <Stack gap="md">
                <FormCheckbox
                  label={tom('roles.label')}
                  name="authorizationRoles"
                  options={Object.values(AuthorizationRoles)
                    .map((r) =>
                      r !== AuthorizationRoles.SuperUser &&
                      r !== AuthorizationRoles.SimpleDataRequest &&
                      (r !== AuthorizationRoles.MarketPartyRegistryReadAll ||
                        canSeeRegistryReadAll)
                        ? {
                            card: true,
                            id: r,
                            label: t(`common.roles.${r}`),
                            value: r,
                          }
                        : undefined,
                    )
                    .filter(isDefined)}
                />
              </Stack>
            </Card>
            <Card
              size="md"
              heading={
                <Stack
                  direction="row"
                  gap="xs"
                  className="text-content-heading"
                >
                  <Text as="h6" variant="h6">
                    {t('common.teams')}{' '}
                  </Text>
                  <InfoDialog title={tom('teams.info.title.about_teams')}>
                    {tom('teams.info_message')}
                  </InfoDialog>
                </Stack>
              }
            >
              <Stack gap="md">
                {isLoadingTeams ? (
                  <SkeletonLines lines={3} />
                ) : data?.items && data.items.length > 0 ? (
                  <FormCheckbox
                    label={tom('teams.label.select_team')}
                    name="teamIds"
                    options={data!.items.map((item) => ({
                      card: true,
                      id: item.id,
                      label: item.name,
                      value: item.id,
                    }))}
                  />
                ) : (
                  <Text>{tom('teams.empty')}</Text>
                )}
              </Stack>
            </Card>
          </Stack>
          {confirmationDialog}
        </form>
      </FormProvider>
    </Page>
  );
};
